import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty, isNil } from 'lodash';
import { ALLOCATIONS_FINAL, ALLOCATIONS_PUBLISHED } from 'common/constants/allocations';
import { useStore } from 'common/store';
import { DialogContextValues } from 'common/types/dialogContext';
import { LayoutContextValues, UseStoreValues } from 'common/types/store';
import { CenterAlignedParagraph } from 'components';
import { ConfirmationDialog } from 'components/Dialogs';
import { DialogContext, LayoutContext } from 'context';
import { getInitialAllocationStatus, InitialAllocationStatus } from 'pages/FundInvestments/utils';
import {
  MAKE_FINAL_ALLOCATION_BUTTON_LABEL,
  MAKE_FINAL_ALLOCATION_CONFIRMATION_MESSAGE,
  PUBLISH_ALLOCATION_BUTTON_LABEL,
  PUBLISH_ALLOCATION_CONFIRMATION_MESSAGE,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { useMakeAllocationsFinal, usePublishAllocationVersion } from 'services/hooks/allocations';
import { useAllocationValuesStore } from 'store';
import { getAllocationStatus, getNumberValue, getObjectValue, handleCatchError } from 'utilities';
import {
  UseFundInvestmentAllocationStatusParams,
  UseFundInvestmentAllocationStatusReturn,
} from './useFundInvestmentAllocationStatus.types';

const useFundInvestmentAllocationStatus = (
  params: UseFundInvestmentAllocationStatusParams
): UseFundInvestmentAllocationStatusReturn => {
  const {
    currentAllocationVersion,
    getValuationByAllocation,
    isCurrentUserAuditor,
    isCurrentValuationFirmOwned,
    isUpdatingValuation,
  } = params;

  const [storeValue] = useStore() as UseStoreValues;
  const { user: currentUser } = storeValue;
  const { is_staff: isCurrentUserStaff = false } = getObjectValue(currentUser);

  const { viewOnlyUser: isCurrentUserViewOnly } = useContext(LayoutContext) as LayoutContextValues;
  const { showDialog } = useContext(DialogContext) as unknown as DialogContextValues;

  // Allocation values store
  const currentAllocationId = useAllocationValuesStore(state => state.currentAllocationId);
  const setCurrentAllocationStatus = useAllocationValuesStore(state => state.setCurrentAllocationStatus);

  // Publish Allocation Version
  const { mutateAsync: publishAllocationVersion } = usePublishAllocationVersion();

  // Make Allocation Version
  const { mutateAsync: makeFinalAllocationVersion } = useMakeAllocationsFinal();

  const [allocationStatus, setAllocationStatus] = useState<InitialAllocationStatus | null>(null);

  const isCurrentAllocationPublished = useMemo<boolean>(() => {
    if (isNil(allocationStatus)) {
      return false;
    }

    const { is_published: isPublished = false } = getObjectValue(allocationStatus);

    return isPublished;
  }, [allocationStatus]);

  const isCurrentAllocationFinal = useMemo<boolean>(() => {
    if (isNil(allocationStatus)) {
      return false;
    }

    const { is_final: isFinal = false } = getObjectValue(allocationStatus);

    return isFinal;
  }, [allocationStatus]);

  const isCurrentAllocationDraft = useMemo<boolean>(() => {
    if (isNil(allocationStatus)) {
      return false;
    }

    return !isCurrentAllocationFinal && !isCurrentAllocationPublished;
  }, [allocationStatus, isCurrentAllocationFinal, isCurrentAllocationPublished]);

  const [isCurrentValuationDisabled, setIsCurrentValuationDisabled] = useState(
    (!isCurrentValuationFirmOwned && !isCurrentUserStaff) || // Scalar valuation and firm user
      isCurrentUserAuditor ||
      isCurrentUserViewOnly ||
      isCurrentAllocationFinal ||
      isUpdatingValuation
  );

  // Publish allocation
  const publishAllocation = useCallback(async () => {
    const version = await publishAllocationVersion({ allocationId: getNumberValue(currentAllocationId) });
    const { published } = getObjectValue(version);

    if (published) {
      getValuationByAllocation().catch(handleCatchError);
      setCurrentAllocationStatus(ALLOCATIONS_PUBLISHED);

      setAllocationStatus(status => ({ ...getInitialAllocationStatus(status), is_published: true }));
    }
  }, [currentAllocationId, getValuationByAllocation, publishAllocationVersion, setCurrentAllocationStatus]);

  const showPublishAllocationDialog = useCallback(() => {
    showDialog({
      actions: [
        {
          callback: publishAllocation,
          label: PUBLISH_ALLOCATION_BUTTON_LABEL,
          type: 'success',
          variant: 'contained',
        },
      ],
      content: CenterAlignedParagraph,
      contentProps: { message: PUBLISH_ALLOCATION_CONFIRMATION_MESSAGE },
      wrapper: ConfirmationDialog,
    });
  }, [publishAllocation, showDialog]);

  // Make allocation final
  const makeAllocationFinal = useCallback(async () => {
    const version = await makeFinalAllocationVersion({ allocationsIds: [getNumberValue(currentAllocationId)] });
    const { updated } = getObjectValue(version);

    if (updated) {
      getValuationByAllocation().catch(handleCatchError);
      setCurrentAllocationStatus(ALLOCATIONS_FINAL);
      setAllocationStatus(status => ({ ...getInitialAllocationStatus(status), is_final: true }));
    }
  }, [currentAllocationId, getValuationByAllocation, makeFinalAllocationVersion, setCurrentAllocationStatus]);

  const showMakeAllocationFinalDialog = useCallback(() => {
    showDialog({
      actions: [
        {
          callback: makeAllocationFinal,
          label: MAKE_FINAL_ALLOCATION_BUTTON_LABEL,
          type: 'success',
          variant: 'contained',
        },
      ],
      content: CenterAlignedParagraph,
      contentProps: { message: MAKE_FINAL_ALLOCATION_CONFIRMATION_MESSAGE },
      wrapper: ConfirmationDialog,
    });
  }, [makeAllocationFinal, showDialog]);

  // Update allocation status
  useEffect(() => {
    if (!isEmpty(currentAllocationVersion)) {
      setAllocationStatus(getInitialAllocationStatus(currentAllocationVersion));
    }
  }, [currentAllocationVersion]);

  // Update store allocation status
  useEffect(() => {
    if (!isNil(isCurrentAllocationFinal) && !isNil(isCurrentAllocationPublished)) {
      const status = getAllocationStatus({
        isFinal: isCurrentAllocationFinal,
        isPublished: isCurrentAllocationPublished,
      });

      setCurrentAllocationStatus?.(status);
    }
  }, [isCurrentAllocationFinal, isCurrentAllocationPublished, setCurrentAllocationStatus]);

  // Update current valuation disabled
  useEffect(() => {
    setIsCurrentValuationDisabled(
      (!isCurrentValuationFirmOwned && !isCurrentUserStaff) || // Scalar valuation and firm user
        isCurrentUserAuditor ||
        isCurrentUserViewOnly ||
        isCurrentAllocationFinal ||
        isUpdatingValuation
    );
  }, [
    isCurrentAllocationFinal,
    isCurrentUserAuditor,
    isCurrentUserStaff,
    isCurrentUserViewOnly,
    isCurrentValuationFirmOwned,
    isUpdatingValuation,
  ]);

  return {
    isCurrentAllocationDraft,
    isCurrentAllocationFinal,
    isCurrentAllocationPublished,
    isCurrentValuationDisabled,
    setIsCurrentValuationDisabled,
    showMakeAllocationFinalDialog,
    showPublishAllocationDialog,
  };
};

export default useFundInvestmentAllocationStatus;
